import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { obterLabelCategoria } from '../utils/categoriaConteudo';
import { JUSTIFICATIVA_PADRAO_RENOMEIO } from '../utils/justificativasPadrao';
import { IRegistroCircularizacao } from '../interfaces/ICircularizacao';

interface Props {
  registro: IRegistroCircularizacao;
  modoExcluidos: boolean;
  selecionado: boolean;
  preparandoCarta: boolean;
  onToggleSelecionado: (id: string) => void;
  onEditar: (registro: IRegistroCircularizacao) => void;
  onRenomearRapido: (registro: IRegistroCircularizacao, novoNome: string, justificativa: string) => void;
  onExcluir: (registro: IRegistroCircularizacao) => void;
  onRestaurar: (registro: IRegistroCircularizacao) => void;
  onEditarJustificativaExclusao: (registro: IRegistroCircularizacao) => void;
  onConsultarJustificativa: (registro: IRegistroCircularizacao) => void;
  onGerenciarContatos: (registro: IRegistroCircularizacao) => void;
  onAbrirPlanejamento: (registro: IRegistroCircularizacao) => void;
  onPrepararCarta: (registro: IRegistroCircularizacao) => void;
  onPreVisualizarCarta: (registro: IRegistroCircularizacao) => void;
  onAbrirCarta: (registro: IRegistroCircularizacao) => void;
}

const BADGE_SITUACAO_REVISAO: Record<string, { label: string; cls: string }> = {
  PENDENTE: { label: 'Pendente', cls: 'badge-light-secondary' },
  REVISADA: { label: 'Revisada', cls: 'badge-light-info' },
  INCLUIDA: { label: 'Incluída', cls: 'badge-light-primary' },
  EXCLUIDA: { label: 'Excluída', cls: 'badge-light-danger' },
};

const BADGE_PREPARACAO: Record<string, { label: string; cls: string }> = {
  NAO_INICIADA: { label: 'Não iniciada', cls: 'badge-light-secondary' },
  EM_ANDAMENTO: { label: 'Em andamento', cls: 'badge-light-warning' },
  PRONTA: { label: 'Carta pronta', cls: 'badge-light-success' },
  NAO_SE_APLICA: { label: 'Não se aplica', cls: 'badge-light-secondary' },
};

const formatarData = (iso?: string) => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.split('-');
  return dia ? `${dia}/${mes}/${ano}` : iso;
};

const truncar = (texto: string, tamanho: number) => (texto.length > tamanho ? `${texto.slice(0, tamanho)}…` : texto);

// Número de colunas da tabela (seção 3.2): Seleção, Empresa, CPF/CNPJ, Categoria, Origem,
// Justificativa, Situação, Carta, Ações.
export const CIRC_TABELA_COLSPAN = 9;

// Seção 3.2 (estrutura da listagem, incluindo a coluna própria de Justificativa) + 3.3/12.2
// (indicador discreto de origem) + 12.3 (edição rápida do nome sem abandonar a lista, com
// justificativa, direto na linha) + 4 (ações: editar/excluir/consultar).
const RegistroCircularizacaoRow: React.FC<Props> = ({
  registro,
  modoExcluidos,
  selecionado,
  preparandoCarta,
  onToggleSelecionado,
  onEditar,
  onRenomearRapido,
  onExcluir,
  onRestaurar,
  onEditarJustificativaExclusao,
  onConsultarJustificativa,
  onGerenciarContatos,
  onAbrirPlanejamento,
  onPrepararCarta,
  onPreVisualizarCarta,
  onAbrirCarta,
}) => {
  const [expandido, setExpandido] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const botaoAcoesRef = useRef<HTMLButtonElement>(null);
  const ALTURA_ESTIMADA_MENU = 260;

  const [renomeioRapido, setRenomeioRapido] = useState<{ nome: string; justificativa: string } | null>(null);

  const alternarMenu = () => {
    if (menuAberto) {
      setMenuAberto(false);
      return;
    }
    const rect = botaoAcoesRef.current?.getBoundingClientRect();
    if (rect) {
      const espacoAbaixo = window.innerHeight - rect.bottom;
      const top = espacoAbaixo >= ALTURA_ESTIMADA_MENU ? rect.bottom + 4 : Math.max(8, rect.top - ALTURA_ESTIMADA_MENU - 4);
      setMenuPos({ top, right: window.innerWidth - rect.right });
    }
    setMenuAberto(true);
  };

  const confirmarRenomeioRapido = () => {
    if (!renomeioRapido) return;
    const nomeAlterado = renomeioRapido.nome.trim();
    if (!nomeAlterado || nomeAlterado === registro.razaoSocialExibicao) {
      setRenomeioRapido(null);
      return;
    }
    if (!renomeioRapido.justificativa.trim()) return;
    onRenomearRapido(registro, nomeAlterado, renomeioRapido.justificativa.trim());
    setRenomeioRapido(null);
  };

  const origemBadge = registro.alteradaPeloAuditor
    ? { cls: 'circ-origem-alterada', label: 'Alterada pelo Auditor', title: 'Dado original alterado pelo auditor' }
    : registro.origemSelecao === 'INCLUSAO_MANUAL'
    ? { cls: 'circ-origem-manual', label: 'Incluída pelo Auditor', title: registro.justificativaInclusaoManual ?? '' }
    : { cls: 'circ-origem-sistema', label: 'Sugerida pelo Sistema', title: registro.justificativaOriginal };

  const situacao = BADGE_SITUACAO_REVISAO[registro.situacaoRevisao];
  const preparacao = BADGE_PREPARACAO[registro.situacaoPreparacaoCarta];
  const contatoPrincipal = registro.contatos.find((c) => c.contatoPrincipal);
  const justificativaExibida = modoExcluidos && registro.justificativaExclusao ? registro.justificativaExclusao : registro.justificativaOriginal;

  return (
    <>
      <tr>
        <td>
          {!modoExcluidos && (
            <input
              type='checkbox'
              className='form-check-input'
              aria-label={`Selecionar ${registro.razaoSocialExibicao}`}
              checked={selecionado}
              onChange={() => onToggleSelecionado(registro.id)}
            />
          )}
        </td>
        <td>
          {renomeioRapido ? (
            <div style={{ minWidth: 220 }}>
              <input
                className='circ-form-control mb-1'
                aria-label={`Novo nome de ${registro.razaoSocialExibicao}`}
                value={renomeioRapido.nome}
                onChange={(e) => setRenomeioRapido({ ...renomeioRapido, nome: e.target.value })}
                autoFocus
              />
              <input
                className='circ-form-control'
                aria-label='Justificativa da alteração'
                placeholder='Justificativa da alteração...'
                value={renomeioRapido.justificativa}
                onChange={(e) => setRenomeioRapido({ ...renomeioRapido, justificativa: e.target.value })}
              />
              <div className='d-flex gap-1 mt-1'>
                <button type='button' className='btn btn-sm btn-primary' aria-label='Confirmar renomeio' onClick={confirmarRenomeioRapido}>
                  <i className='bi bi-check-lg' />
                </button>
                <button type='button' className='btn btn-sm btn-light' aria-label='Cancelar renomeio' onClick={() => setRenomeioRapido(null)}>
                  <i className='bi bi-x-lg' />
                </button>
              </div>
              {!renomeioRapido.justificativa.trim() && renomeioRapido.nome.trim() !== registro.razaoSocialExibicao && (
                <div className='text-danger mt-1' style={{ fontSize: 11 }}>Justificativa obrigatória para confirmar.</div>
              )}
            </div>
          ) : (
            <>
              <button
                type='button'
                className='btn btn-link p-0 text-start'
                style={{ fontWeight: 600, fontSize: 13 }}
                onClick={() => setExpandido((v) => !v)}
              >
                <i className={`bi bi-chevron-${expandido ? 'down' : 'right'} me-1`} style={{ fontSize: 11 }} />
                {registro.razaoSocialExibicao}
              </button>
              {!modoExcluidos && (
                <button
                  type='button'
                  className='btn btn-sm btn-icon p-0 ms-1'
                  title='Renomear rapidamente'
                  style={{ fontSize: 11, color: '#8a92a3' }}
                  onClick={() => setRenomeioRapido({ nome: registro.razaoSocialExibicao, justificativa: JUSTIFICATIVA_PADRAO_RENOMEIO })}
                >
                  <i className='bi bi-pencil' />
                </button>
              )}
              {registro.nomeFantasia && <div className='text-muted fs-9'>{registro.nomeFantasia}</div>}
            </>
          )}
        </td>
        <td>{registro.cnpj}</td>
        <td>{obterLabelCategoria(registro.categoria)}</td>
        <td>
          <button
            type='button'
            className={`circ-origem-badge ${origemBadge.cls}`}
            title={origemBadge.title}
            onClick={() => onConsultarJustificativa(registro)}
          >
            {origemBadge.label}
          </button>
        </td>
        <td style={{ maxWidth: 220 }}>
          <button
            type='button'
            className='btn btn-link p-0 text-start text-wrap'
            style={{ fontSize: 12, color: '#47586b' }}
            title='Ver justificativa completa'
            onClick={() => onConsultarJustificativa(registro)}
          >
            {truncar(justificativaExibida, 70)}
          </button>
        </td>
        <td>
          <span className={`badge ${situacao.cls}`}>{situacao.label}</span>
        </td>
        <td>
          <span className={`badge ${preparacao.cls}`}>{preparacao.label}</span>
        </td>
        <td className='text-end'>
          <div className='circ-actions-menu-wrap' style={{ position: 'relative', display: 'inline-block' }}>
            <button ref={botaoAcoesRef} type='button' className='btn btn-sm btn-icon btn-light' title='Ações' onClick={alternarMenu}>
              <i className='bi bi-three-dots-vertical' />
            </button>
            {menuAberto && menuPos && createPortal(
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                  onClick={() => setMenuAberto(false)}
                />
                <div
                  className='circ-actions-menu'
                  style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                >
                  <button type='button' onClick={() => { setMenuAberto(false); onConsultarJustificativa(registro); }}>
                    <i className='bi bi-info-circle me-2' /> Consultar Justificativa
                  </button>
                  {!modoExcluidos && (
                    <>
                      <button type='button' onClick={() => { setMenuAberto(false); onEditar(registro); }}>
                        <i className='bi bi-pencil me-2' /> Editar Empresa
                      </button>
                      <button type='button' onClick={() => { setMenuAberto(false); onGerenciarContatos(registro); }}>
                        <i className='bi bi-person-lines-fill me-2' /> Gerenciar Contatos
                      </button>
                      <button type='button' onClick={() => { setMenuAberto(false); onAbrirPlanejamento(registro); }}>
                        <i className='bi bi-calendar-event me-2' /> Planejar Envio/Resposta
                      </button>
                      <button
                        type='button'
                        disabled={preparandoCarta}
                        onClick={() => { setMenuAberto(false); onPrepararCarta(registro); }}
                      >
                        <i className='bi bi-file-earmark-check me-2' /> Preparar Carta
                      </button>
                      <button
                        type='button'
                        disabled={registro.situacaoPreparacaoCarta !== 'PRONTA'}
                        onClick={() => { setMenuAberto(false); onPreVisualizarCarta(registro); }}
                      >
                        <i className='bi bi-printer me-2' /> Pré-visualizar/Imprimir
                      </button>
                      <button
                        type='button'
                        disabled={registro.situacaoPreparacaoCarta !== 'PRONTA'}
                        onClick={() => { setMenuAberto(false); onAbrirCarta(registro); }}
                      >
                        <i className='bi bi-send-check me-2' /> Carta e Envio
                      </button>
                      <button type='button' className='text-danger' onClick={() => { setMenuAberto(false); onExcluir(registro); }}>
                        <i className='bi bi-trash me-2' /> Excluir da Circularização
                      </button>
                    </>
                  )}
                  {modoExcluidos && (
                    <>
                      <button type='button' onClick={() => { setMenuAberto(false); onRestaurar(registro); }}>
                        <i className='bi bi-arrow-counterclockwise me-2' /> Restaurar Empresa
                      </button>
                      <button type='button' onClick={() => { setMenuAberto(false); onEditarJustificativaExclusao(registro); }}>
                        <i className='bi bi-pencil-square me-2' /> Editar Justificativa da Exclusão
                      </button>
                    </>
                  )}
                </div>
              </>,
              document.body
            )}
          </div>
        </td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={CIRC_TABELA_COLSPAN}>
            <div className='circ-linha-expandida'>
              <div className='row'>
                <div className='col-md-6 circ-secao-expandida'>
                  <div className='circ-secao-expandida-titulo'>Contatos Responsáveis</div>
                  {registro.contatos.length === 0 && (
                    <div className='text-muted' style={{ fontSize: 12.5 }}>Nenhum contato cadastrado ainda.</div>
                  )}
                  {registro.contatos.map((c) => (
                    <div key={c.id} className='circ-contato-item'>
                      <div className='circ-contato-info'>
                        <div className='circ-contato-nome'>
                          {c.nomeCompleto} {c.contatoPrincipal && <span className='badge badge-light-primary ms-1'>Principal</span>}
                        </div>
                        <div className='circ-contato-meta'>{c.emailPrincipal} · {c.telefone}</div>
                      </div>
                    </div>
                  ))}
                  {!modoExcluidos && (
                    <button type='button' className='btn btn-sm btn-light-primary mt-1' onClick={() => onGerenciarContatos(registro)}>
                      <i className='bi bi-plus-lg me-1' /> Gerenciar Contatos
                    </button>
                  )}
                </div>
                <div className='col-md-6 circ-secao-expandida'>
                  <div className='circ-secao-expandida-titulo'>Planejamento</div>
                  <div style={{ fontSize: 12.5 }}>
                    <div><strong>Contato responsável:</strong> {contatoPrincipal?.nomeCompleto ?? '—'}</div>
                    <div><strong>Data prevista de envio:</strong> {formatarData(registro.dataPrevistaEnvio)}</div>
                    <div><strong>Prazo para resposta:</strong> {formatarData(registro.prazoResposta)}</div>
                  </div>
                  {!modoExcluidos && (
                    <button type='button' className='btn btn-sm btn-light-primary mt-2' onClick={() => onAbrirPlanejamento(registro)}>
                      <i className='bi bi-calendar-event me-1' /> Definir Prazos
                    </button>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default RegistroCircularizacaoRow;
